import { Controller } from "@hotwired/stimulus";
import { Map } from "@maptiler/sdk";
import * as maptilersdk from "@maptiler/sdk";

export { maptilersdk };

// Clés pour le stockage local
const MAP_POSITION_KEY = "santiagooo_map_position";
const MAP_ZOOM_KEY = "santiagooo_map_zoom";

export default class extends Controller {
  static values = {
    latitude: Number,
    longitude: Number,
    zoom: Number,
    maptilerKey: String,
  };

  initialize() {
    this.mapInitialized = false;
    
    // Récupérer la position et le zoom sauvegardés
    this.savedPosition = this.getSavedMapPosition();
    this.savedZoom = this.getSavedMapZoom();
    
    // Lier les méthodes pour éviter les problèmes de contexte
    this.saveMapState = this.saveMapState.bind(this);
  }

  connect() {
    if (!this.hasMaptilerKeyValue) {
      return;
    }

    maptilersdk.config.apiKey = this.maptilerKeyValue;
    this.initializeMap();
  }

  initializeMap() {
    if (this.mapInitialized || !this.mapElement) {
      console.log("🗺 Sortie précoce de initializeMap:", {
        mapInitialized: this.mapInitialized,
        mapElement: !!this.mapElement
      });
      return;
    }

    try {
      // Utiliser la position et le zoom sauvegardés s'ils existent
      const center = this.savedPosition || [this.longitudeValue, this.latitudeValue];
      const zoom = this.savedZoom || this.zoomValue;
      
      this.map = new Map({
        container: this.mapElement,
        style: `https://api.maptiler.com/maps/dde387fc-1237-42da-8767-a16e8c6d4304/style.json?key=${this.maptilerKeyValue}`,
        center: center,
        zoom: zoom,
        maxZoom: 15,
        minZoom: 11
      });

      // Ajoute un événement 'load' pour s'assurer que la carte est bien initialisée
      this.map.once('load', () => {
        console.log("🗺 Carte MapTiler initialisée avec succès");
        this.mapInitialized = true;

        // Émet l'événement map-initialized une seule fois
        document.dispatchEvent(new CustomEvent("map-initialized", {
          detail: { map: this.map }
        }));
        
        // Ajouter un écouteur pour sauvegarder l'état de la carte lors des mouvements
        this.map.on('moveend', this.saveMapState);

        // Ajoute les contrôles de navigation
        this.map.addControl(new maptilersdk.NavigationControl(), 'bottom-right');
        this.map.addControl(new maptilersdk.GeolocateControl({
            positionOptions: { enableHighAccuracy: true },
            trackUserLocation: true
        }), 'bottom-right');
      });
    } catch (error) {
      console.error("❌ Erreur lors de l'initialisation de la carte MapTiler:", error);
      this.mapInitialized = false;
    }
  }
  
  // Sauvegarder l'état actuel de la carte
  saveMapState() {
    if (!this.map) return;
    
    const center = this.map.getCenter();
    const zoom = this.map.getZoom();
    
    // Sauvegarder dans le localStorage
    localStorage.setItem(MAP_POSITION_KEY, JSON.stringify([center.lng, center.lat]));
    localStorage.setItem(MAP_ZOOM_KEY, zoom.toString());
    
    console.log("🗺 État de la carte sauvegardé:", [center.lng, center.lat], zoom);
  }
  
  // Récupérer la position sauvegardée
  getSavedMapPosition() {
    try {
      const savedPosition = localStorage.getItem(MAP_POSITION_KEY);
      return savedPosition ? JSON.parse(savedPosition) : null;
    } catch (error) {
      console.error("❌ Erreur lors de la récupération de la position sauvegardée:", error);
      return null;
    }
  }
  
  // Récupérer le zoom sauvegardé
  getSavedMapZoom() {
    try {
      const savedZoom = localStorage.getItem(MAP_ZOOM_KEY);
      return savedZoom ? parseFloat(savedZoom) : null;
    } catch (error) {
      console.error("❌ Erreur lors de la récupération du zoom sauvegardé:", error);
      return null;
    }
  }

  // Ajouter une propriété pour éviter les appels multiples
  refreshTimeout = null;
  lastRefresh = 0;
  isRefreshing = false;

  refreshMap() {
    // Éviter les appels trop fréquents ou pendant un rafraîchissement en cours
    const now = Date.now();
    if (now - this.lastRefresh < 500 || this.isRefreshing) { // Limiter à un appel toutes les 500ms
      return;
    }
    
    this.lastRefresh = now;
    this.isRefreshing = true;
    
    // Si la carte n'est pas initialisée, on l'initialise
    if (!this.map || !this.mapInitialized) {
      console.log("🗺 Map not initialized yet, initializing...");
      this.initializeMap();
      this.isRefreshing = false;
      return;
    }
    
    try {
      // Forcer la carte à se redessiner avec MapTiler SDK
      this.map.resize();
      console.log("🗺 Map resized successfully");
    } catch (error) {
      console.error("❌ Error refreshing map:", error);
    } finally {
      this.isRefreshing = false;
    }
  }

  disconnect() {
    console.log("🗺 Map Controller: disconnect()");
    
    // Sauvegarder l'état de la carte avant de la supprimer
    if (this.map) {
      this.saveMapState();
      
      // Supprimer l'écouteur d'événement
      this.map.off('moveend', this.saveMapState);
      
      // Nettoyer la carte
      this.map.remove();
      this.map = null;
      this.mapInitialized = false;
    }
    
    // Nettoyer le timeout s'il existe
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
  }

  get mapElement() {
    const element = this.element.querySelector("#map");
    if (!element) {
      console.error("❌ L'élément #map n'a pas été trouvé dans le DOM.");
    }
    return element;
  }
}